import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { post, get } from 'src/services/httpClient.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

interface AddCountryProps {
  handleClose: () => void;
  fetchCountries: () => void;
  selectedVendorId: string; // Add this prop
}

const BoardAddCountry: React.FC<AddCountryProps> = ({
  handleClose,
  fetchCountries,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const { showSnackbar } = useSnackbar();
  const [vendorsList, setVendors] = useState<any[]>([]);
  const APIURL = process.env.REACT_APP_BASE_API_URL;

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    shortCode: '',
    phoneCode: '',
    isActive: true,
    vendorId: selectedVendorId || '' // Initialize with passed vendor ID
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name as string]: value }));
  };

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  const fetchVendors = async () => {
    try {
      const response = await get(`${APIURL}/api/vendors`);
      setVendors(response?.data || response);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      showSnackbar('Failed to load vendors.', 'error');
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  /* =========================
     Update form when selectedVendorId prop changes
  ========================= */
  useEffect(() => {
    setForm(prev => ({ ...prev, vendorId: selectedVendorId }));
  }, [selectedVendorId]);

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, isActive: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!form.name) {
      showSnackbar('Country name is required', 'warning');
      return;
    }

    if (!form.vendorId) {
      showSnackbar('Vendor is required', 'warning');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: form.name,
        shortCode: form.shortCode || null,
        phoneCode: form.phoneCode || null,
        isActive: form.isActive,
        vendorId: form.vendorId
      };

      const res = await post(
        `${APIURL}/api/miscellaneous/country`,
        payload
      );
      
      if (res?.statusCode === 200) {
        showSnackbar(res?.message || 'Country added successfully', 'success');
        fetchCountries();
        handleClose();
      } else {
        showSnackbar(res?.message || 'Failed to add country', 'error');
      }
    } catch (error: any) {
      console.error('Failed to add country', error);
      showSnackbar(error?.message || 'Failed to add country', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendorsList.find((v: any) => v.vendorId === selectedVendorId);

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add Country
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor-label">Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              label="Vendor"
              value={selectedVendorId || ""}
              disabled
              inputProps={{ readOnly: true }}
            >
              <MenuItem value={selectedVendorId || ""}>
                {selectedVendor?.vendorName || selectedVendorId || "Select Vendor"}
              </MenuItem>
            </Select>
            <FormHelperText>Vendor is selected from the parent list</FormHelperText>
          </FormControl>
          <input type="hidden" name="vendorId" value={form.vendorId} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Country Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Short Code"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Phone Code"
            name="phoneCode"
            value={form.phoneCode}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={handleSwitchChange}
              />
            }
            label="Is Active"
          />
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardAddCountry;